/**
 * ProgressRing Component
 *
 * A circular progress indicator using SVG.
 * Shows completion percentage with optional center label.
 *
 * @example
 * ```tsx
 * <ProgressRing percent={75} label="3/4" size={32} />
 * ```
 */

import { type CSSProperties, useMemo } from 'react';

/**
 * Props for ProgressRing component
 */
export interface ProgressRingProps {
  /** Progress percentage (0-100) */
  percent: number;
  /** Size of the ring in pixels (default: 32) */
  size?: number;
  /** Stroke width in pixels (default: 3) */
  strokeWidth?: number;
  /** Color of the progress arc (default: primary color) */
  progressColor?: string;
  /** Color of the background circle (default: border color) */
  backgroundColor?: string;
  /** Optional label to display in center */
  label?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * ProgressRing displays a circular progress indicator
 */
export const ProgressRing = ({
  percent,
  size = 32,
  strokeWidth = 3,
  progressColor = 'var(--color-primary, #3b82f6)',
  backgroundColor = 'var(--color-border, #e5e7eb)',
  label,
  className,
}: ProgressRingProps) => {
  // Clamp percent between 0 and 100
  const clampedPercent = Math.max(0, Math.min(100, percent));

  // Calculate SVG parameters
  const { radius, circumference, strokeDashoffset } = useMemo(() => {
    const r = (size - strokeWidth) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (clampedPercent / 100) * c;
    return { radius: r, circumference: c, strokeDashoffset: offset };
  }, [size, strokeWidth, clampedPercent]);

  const center = size / 2;

  return (
    <div style={containerStyle} className={className}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={svgStyle}>
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={progressCircleStyle}
        />
      </svg>
      {label && (
        <span
          style={{
            ...labelStyle,
            fontSize: size * 0.25,
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const svgStyle: CSSProperties = {
  transform: 'rotate(-90deg)',
};

const progressCircleStyle: CSSProperties = {
  transition: 'stroke-dashoffset 0.3s ease',
};

const labelStyle: CSSProperties = {
  position: 'absolute',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

export default ProgressRing;
